
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// constants
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'
import { EMPLOYEE_DICTIONARIES_TYPES } from '@/core/constants/users.constants'
import { users } from '@/core/realisations/users.realisation'
import { DataState } from '@/core/classes/DataState.class'

// types
import { CityT } from '@/core/types/cities.types'
import { UserDictionaryT } from '@/core/types/users.types'
import { cities } from '@/core/realisations/cities.realisation'

export default defineComponent({
    components: {
        TheInput,
        TheButton,
        TheSelect,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['reset', 'search'],
    setup(_, { emit }) {
        const state = reactive({
            search: null as string | null,
            organizationId: null as number | null,
            departmentId: null as number | null,
            cityId: null as number | null,
            subdivisionId: null as number | null,
            dateOfReceiptFrom: null as string | null,
            dateOfReceiptTo: null as string | null,
            dateOfBirthFrom: null as string | null,
            dateOfBirthTo: null as string | null,
            jobId: null as number | null,
            email: null as string | null,
            phoneNumber: null as string | null,
            workPhone: null as string | null,
            statusId: null as number | null,
        })

        const data_state = reactive({
            cities: new DataState<CityT>({}),
            //
            departments: new DataState<UserDictionaryT>({}),
            jobs: new DataState<UserDictionaryT>({}),
            organizations: new DataState<UserDictionaryT>({}),
            statuses: new DataState<UserDictionaryT>({}),
            subdivisions: new DataState<UserDictionaryT>({}),
        })

        //

        const reset = () => {
            state.search = null
            state.organizationId = null
            state.departmentId = null
            state.cityId = null
            state.subdivisionId = null
            state.dateOfReceiptFrom = null
            state.dateOfReceiptTo = null
            state.dateOfBirthFrom = null
            state.dateOfBirthTo = null
            state.jobId = null
            state.email = null
            state.phoneNumber = null
            state.workPhone = null
            state.statusId = null

            emit('reset')
        }

        const getFormattedDate = (date: string | null) => {
            if (!date) return null

            const date_arr = date.split('-')
            return `${date_arr[1]}-${date_arr[2]}`
        }

        const search = () => {
            const dateOfBirthFrom = getFormattedDate(state.dateOfBirthFrom)
            const dateOfBirthTo = getFormattedDate(state.dateOfBirthTo)

            emit('search', {
                ...state,
                dateOfBirthFrom,
                dateOfBirthTo,
            })
        }

        // dictionaries

        const getDictionary = (type: number) => {
            switch (type) {
                case 1:
                    return data_state.departments
                case 2:
                    return data_state.jobs
                case 3:
                    return data_state.organizations
                case 4:
                    return data_state.statuses
                case 5:
                    return data_state.subdivisions
                default:
                    return null
            }
        }

        const loadDictionary = async (type: number, description?: string) => {
            let dictionary = getDictionary(type)

            if (!dictionary) return

            try {
                dictionary.is_loading = true

                const response = await users.getDictionary(type, description)
                dictionary.list = response.data

                dictionary.is_failed = false
                dictionary.failure.error_state.is_cancel = false
            } catch (error) {
                dictionary.failure.catchError(error)

                if (!dictionary.failure.error_state.is_cancel) {
                    dictionary.is_failed = true
                }
            } finally {
                if (!dictionary.failure.error_state.is_cancel) {
                    dictionary.is_loading = false
                }
            }
        }

        const loadAllDictionaries = () => {
            EMPLOYEE_DICTIONARIES_TYPES.forEach((dictionary) => loadDictionary(dictionary.id))
        }

        // cities

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCity = (search_query: string) => {
            // data_state.cities.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: CityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        const searchJob = (search_query: string) => {
            loadDictionary(2, search_query)
        }

        const searchDictionaryOne = (description: string) => {
            loadDictionary(1, description)
        }

        const searchDictionaryThree = (description: string) => {
            loadDictionary(3, description)
        }

        const searchDictionaryFour = (description: string) => {
            loadDictionary(4, description)
        }

        const searchDictionaryFive = (description: string) => {
            loadDictionary(5, description)
        }

        //

        onBeforeMount(() => {
            loadCities(1)
            loadAllDictionaries()
        })

        return {
            state,
            data_state,
            reset,
            search,
            //
            loadCities,
            loadMoreCities,
            searchCity,
            unshiftFindedCity,
            searchJob,
            //
            loadDictionary,
            searchDictionaryThree,
            searchDictionaryOne,
            searchDictionaryFour,
            searchDictionaryFive,
            //
            PHONE_NUMBER_MASK,
        }
    },
})
